import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import CONTACTMANAGEMENT_CONSTANTS from "lookup/ContactManagementConstants";
import { sendAuthenticatedAsyncRequest } from 'services/AsyncRequestService';
import { showNotification } from 'services/NotificationService';
import { getErrorMessage } from "services/UtilityService";
import { DeliveryServiceConstants } from "lookup/DeliveryServiceConstants";

const ContactValueField = () => {
  const { values, errors, touched, handleChange } = useFormikContext();
  const [label, setLabel] = useState("Fax Number");

  useEffect(() => {
    setLabel(values.contactType === DeliveryServiceConstants.DELIVERY_SERVICE_TYPES.EMAIL ? "Email Address" : "Fax Number");
  }, [values.contactType]);

  return (
    <Form.Group controlId="contactValue" className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="text"
        name="contactValue"
        value={values.contactValue}
        onChange={handleChange}
        isInvalid={touched.contactValue && !!errors.contactValue}
        placeholder={
          values.contactType === DeliveryServiceConstants.DELIVERY_SERVICE_TYPES.EMAIL ? "Enter email address" : "Enter fax number"
        }
      />
      <Form.Control.Feedback type="invalid">
        {errors.contactValue}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

const ContactModal = ({ show, handleClose, createdBy, contactToEdit, onActionComplete}) => {
  const isEditMode = !!contactToEdit; 

  const contactSchema = Yup.object().shape({
    contactValue: Yup.string()
      .test('contactValue-validation', function(value) {
        const { contactType } = this.parent;
        
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return this.createError({ message: "Contact value is required" });
        }
        
        if (contactType === DeliveryServiceConstants.DELIVERY_SERVICE_TYPES.FAX) {
          // Validate as phone number (digits only, 10 digits)
          if (!/^[0-9]*$/.test(value)) {
            return this.createError({ message: CONTACTMANAGEMENT_CONSTANTS.numberValidationError });
          }
          if (value.length !== 10) {
            return this.createError({ message: CONTACTMANAGEMENT_CONSTANTS.fieldLengthValidationPrefix + "10" });
          }
        } else {
          // Validate as email
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return this.createError({ message: "Enter a valid email" });
          }
          if (value.length > 255) {
            return this.createError({ message: CONTACTMANAGEMENT_CONSTANTS.fieldMaxLengthValidationPrefix + "255" });
          }
        }
        
        return true;
      }),
    contactType: Yup.string()
      .oneOf([DeliveryServiceConstants.DELIVERY_SERVICE_TYPES.FAX, DeliveryServiceConstants.DELIVERY_SERVICE_TYPES.EMAIL], "Must be either FAX or EMAIL")
      .required("Contact type is required"),
    description: Yup.string().required("Notes are required"),
  });

  const initialValues = {
    contactValue: contactToEdit?.contactValue || "",
    contactType: contactToEdit?.contactType || DeliveryServiceConstants.DELIVERY_SERVICE_TYPES.FAX,
    description: contactToEdit?.description || "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = { ...values, createdBy };
    const url = isEditMode
      ? `/blacklisted-contacts/${contactToEdit.id}`
      : `/blacklisted-contacts`;
    const method = isEditMode ? "PUT" : "POST";

    if (isEditMode) {
      payload.previousContactValue = initialValues.contactValue;
      payload.previousContactType = initialValues.contactType;
    }

    sendAuthenticatedAsyncRequest(
      url,
      method,
      payload,
      () => {
        setSubmitting(false);
        showNotification({
          title: 'Action Successful!',
          message: isEditMode
            ? 'Blacklist contact updated successfully.'
            : 'Blacklist contact created successfully.',
          position: 'tr',
          type: 'success',
        });
        handleClose(true);
        onActionComplete?.();
      },
      (errorResponse) => {
        setSubmitting(false);
        const errorMessage = getErrorMessage(errorResponse?.data?.errorCode) ||
          getErrorMessage(
            isEditMode ? 'UPDATE_BLACKLISTED_ENTRY_FAILED' : 'BLACKLIST_ENTRY_CREATION_FAILED'
          );
        showNotification({
          title: 'Error!',
          message: errorMessage,
          position: 'tr',
          type: 'error',
        });
      }
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode ? "Update Blacklisted Contact" : "Blacklist Contact"}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        validationSchema={contactSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
          <>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={12}>
                    <Form.Group controlId="contactType" className="mb-3">
                      <Form.Label>Contact Type</Form.Label>
                      <Form.Control
                        as="select"
                        name="contactType"
                        value={values.contactType}
                        onChange={handleChange}
                        isInvalid={touched.contactType && !!errors.contactType}
                        disabled={isEditMode}
                      >
                        <option value="FAX">FAX</option>
                        <option value="EMAIL">EMAIL</option>
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.contactType}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <ContactValueField />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group controlId="description" className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        isInvalid={touched.description && !!errors.description}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>

            <Modal.Footer>
              <Button
                type="submit"
                variant="success"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isEditMode ? "Update" : "Submit"}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default ContactModal;

